import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, defineAsyncComponent, PropType, getCurrentInstance, onMounted } from 'vue';
import VueI18n from 'vue-i18n';

// Error message can be targeted at specific fields or all three fields.
type ErrorMessageObject = {
  day?: VueI18n.TranslateResult;
  month?: VueI18n.TranslateResult;
  year?: VueI18n.TranslateResult;
  all?: VueI18n.TranslateResult;
}

// Components

export default /*#__PURE__*/_defineComponent({
  __name: 'date-input',
  props: {
  value: {
    // String is only used here so that we can accept an emptry string as an initial value
    // Date should always be preferred
    type: [Date, String] as PropType<Date | string>,
    default: '',
  },
  label: {
    type: [String, Object] as PropType<VueI18n.TranslateResult | string>,
    default: (getCurrentInstance()?.proxy as Vue)?.$t('components.dateTimePicker.date') as string ?? 'Date',
  },
  locale: {
    type: String,
    default: 'en-GB',
  },
  error: {
    type: Boolean,
    default: false
  },
  errorMessage: {
    type: Object as PropType<ErrorMessageObject>,
    default: () => {},
  },
  //for disabling future dates (like in the case of date of birth)
  disableFutureDates: {
    type: Boolean,
    default: false,
  },
  maxAge: {
    type: Number,
    default: null,
  },
  minAgeMonths: {
    type: Number,
    default: null,
  }
},
  emits: ["input", "blur", "valid"],
  setup(__props, { emit }: { emit: ({
  (event: 'input', value: Date | null): void;
  (blur: 'blur'): void;
  (event: 'valid', value: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const IconError = defineAsyncComponent(
  () => import('@white-label-icon/icon-error')
);

// Instance proxy for translations
const proxy = getCurrentInstance()?.proxy as Vue;

// Props


// Emits


// Internal state so that we can sanitize and add leading zeros
const inputOne = ref('');
const inputTwo = ref('');
const inputThree = ref('');

// If the user has blurred the year input, we can start validating the date
const yearHasBeenBlurred = ref(false);

// Check if current locale uses day first or month first format
const dayFirst = computed(() => {
  const localesWithMonthFirst = ['en-US'];
  return !localesWithMonthFirst.includes(props.locale);
});

// Get date parts for use in HTML attributes
const dateParts = computed(() => {
  return dayFirst.value ? ['day', 'month', 'year'] : ['month', 'day', 'year'];
});

// Number values for day, month, and year
const day = computed(() => {
  return dayFirst.value ? +inputOne.value : +inputTwo.value;
})
const month = computed(() => {
  return dayFirst.value ? +inputTwo.value : +inputOne.value;
})
const year = computed(() => {
  return +inputThree.value;
});

// Generated Date object based on current input values
const date = computed(() => {
  return new Date(
    year.value,
    month.value - 1,
    day.value
  );
});

//calculate age from date
const calculateAge = (birthDate: Date): number => {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};

//calculate age in months from date
const calculateAgeInMonths = (birthDate: Date): number => {
  const today = new Date();
  const months = (today.getFullYear() - birthDate.getFullYear()) * 12;
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  let totalMonths = months + monthDiff;
  if (dayDiff < 0) {
    totalMonths--;
  }

  return totalMonths;
};

const validateDateComponents = (day: number, month: number, year: number, proxy: any): ErrorMessageObject | null => {
  if (day > 31 || day < 1) {
    return { day: proxy.$t('UI.dateInput.formErrors.invalidDay') };
  }
  if (month > 12 || month < 1) {
    return { month: proxy.$t('UI.dateInput.formErrors.invalidMonth') };
  }
  if (year < 1000 || year > 9999) {
    return { year: proxy.$t('UI.dateInput.formErrors.invalidYear') };
  }
  return null;
};

const validateFullDate = (date: Date, day: number, month: number, year: number, proxy: any): ErrorMessageObject | null => {
  if (date.getDate() !== day || date.getMonth() !== (month - 1) || date.getFullYear() !== year) {
    return { all: proxy.$t('UI.dateInput.formErrors.invalidDate')};
  }
  return null;
};

const validateFutureDate = (date: Date, disableFutureDates: boolean, proxy: any): ErrorMessageObject | null => {
    if (disableFutureDates && date > new Date()) {
    return { all: proxy.$t('UI.dateInput.formErrors.dateInThePast') };
  }
  return null;
}

const validateMinAgeMonths = (date: Date, minAgeMonths: number | null, disableFutureDates: boolean, proxy: any): ErrorMessageObject | null => {
  if (minAgeMonths !== null && disableFutureDates) {
    const ageInMonths = calculateAgeInMonths(date);
    if (ageInMonths < minAgeMonths) {
      return { all: proxy.$t('UI.dateInput.formErrors.minimumAgeMonths', { minMonth: minAgeMonths }) };
    }
  }
  return null;
};

const validateMaxAge = (date: Date, maxAge: number | null, disableFutureDates: boolean, proxy: any): ErrorMessageObject | null => {
  if (maxAge !== null && disableFutureDates) {
    const age = calculateAge(date);
    if (age > maxAge) {
      return { all: proxy.$t('UI.dateInput.formErrors.providerMinimumYear', { maxAge: maxAge, provider: 'battleface' }) };
    }
  }
  return null;
};

// Generate error message if the date is invalid
const internalErrorMessages = computed(() => {
  // Don't validate until user has attempted to enter the year
  if (!yearHasBeenBlurred.value && !props.error) return null;

  const componentError = validateDateComponents(day.value, month.value, year.value, proxy);
  if(componentError) {
    return componentError;
  }

  if (day.value && month.value && year.value) {

    //full date validation
    const fullDateError = validateFullDate(date.value, day.value, month.value, year.value, proxy);
    if (fullDateError) {
      return fullDateError;
    }

    //for validation of dates in the future
    const futureDateError = validateFutureDate(date.value, props.disableFutureDates, proxy);
    if (futureDateError) {
      return futureDateError;
    }

    //check for minimum age in months
    const minAgeError = validateMinAgeMonths(date.value, props.minAgeMonths, props.disableFutureDates, proxy);
    if (minAgeError) {
      return minAgeError;
    }

    //for validation of maxAge
    const maxAgeError = validateMaxAge(date.value, props.maxAge, props.disableFutureDates, proxy);
    if (maxAgeError) {
      return maxAgeError;
    }
  }
  return null;
});

// Pull first error message from internal errors
const internalErrorMessage = computed(() => {
  if (internalErrorMessages.value) {
    const errorKeys = Object.keys(internalErrorMessages.value) as (keyof ErrorMessageObject)[];
    if (errorKeys.length > 0) {
      return internalErrorMessages.value?.[errorKeys[0]];
    }
  }
  return '';
});

// Pull first error message from external errors
const externalErrorMessage = computed(() => {
  if (!props.errorMessage || typeof props.errorMessage !== 'object') return '';
  const keys = Object.keys(props.errorMessage) as (keyof ErrorMessageObject)[];
  if (keys.length === 0) return '';
  return props.errorMessage[keys[0]] ?? '';
});

// Show error message based on the field
const showError = (field: string): boolean => {
  if (props.error && yearHasBeenBlurred.value) {
    if(!isValid.value) return true;
  }
  if (props.errorMessage?.all || internalErrorMessages?.value?.all) return true;
  if (field === 'day') {
    return !!(internalErrorMessages.value?.day
      ?? props.errorMessage?.day);
  } else if (field === 'month') {
    return !!(internalErrorMessages.value?.month
      ?? props.errorMessage?.month);
  } else {
    return !!(internalErrorMessages.value?.year
      ?? props.errorMessage?.year);
  }
};

// Replace non-numeric characters
const sanitizeNumbers = (value: string) => {
  return value.replace(/\D/g, '')
};

// Add a leading zero to single-digit values
const addLeadingZero = (value: string) => {
  if (value.length === 1 && value !== '0') {
    return `0${value}`;
  }
  return value;
};

// Remove non-numeric characters, and emit the resulting Date
const onInput = (value: string, field: 'one' | 'two' | 'three') => {
  if (field === 'one') {
    inputOne.value = sanitizeNumbers(value);
  } else if (field === 'two') {
    inputTwo.value = sanitizeNumbers(value);
  } else if (field === 'three') {
    inputThree.value = sanitizeNumbers(value);

    if (inputThree.value.length >= 4) {
      yearHasBeenBlurred.value = true;
    }
  }

  if (isValid.value && date.value) {
    emit('input', date.value);
  } else {
    emit('input', null);
  }
  //emits validation status
  emit('valid', isValid.value)
}

const isValid = computed(() => {
  //checking if all fields are filled in
  if(!inputOne.value || !inputTwo.value || !inputThree.value) {
    return false;
  }

  if(inputThree.value.length < 4) {
    return false;
  }

  //check for internal errors
  if(internalErrorMessages.value) {
    return false;
  }

  const date = new Date(year.value, month.value -1, day.value);

  //check if date is valid
  if(isNaN(date.getTime())) {
    return false;
  }

  //check if date components match input
  if(date.getDate() !== day.value || date.getMonth() !== month.value -1 || date.getFullYear() !== year.value) {
    return false;
  }

  //check future dates if disabled
  if(props.disableFutureDates && date > new Date()) {
    return false;
  }

  if (props.minAgeMonths !== null && props.disableFutureDates) {
    const ageInMonths = calculateAgeInMonths(date);
    if (ageInMonths < props.minAgeMonths) {
      return false;
    }
  }

  //check maximum age if specified
  if(props.maxAge !== null && props.disableFutureDates) {
    const age = calculateAge(date);
    if (age > props.maxAge || age < 0) {
      return false;
    }
  }

  return true;

})

onMounted(() => {
  let date: Date | null = null;
  if (props.value instanceof Date) {
    date = props.value;
  } else if (typeof props.value === 'string' && props.value) {
    const parsed = new Date(props.value);
    if (!isNaN(parsed.getTime())) {
      date = parsed;
    }
  }
  if (date) {
    if (dayFirst.value) {
      inputOne.value = String(date.getDate()).padStart(2, '0');
      inputTwo.value = String(date.getMonth() + 1).padStart(2, '0');
    } else {
      inputOne.value = String(date.getMonth() + 1).padStart(2, '0');
      inputTwo.value = String(date.getDate()).padStart(2, '0');
    }
    inputThree.value = String(date.getFullYear());
    emit('valid', isValid.value);
  }
});

return { __sfc: true,IconError, proxy, props, emit, inputOne, inputTwo, inputThree, yearHasBeenBlurred, dayFirst, dateParts, day, month, year, date, calculateAge, calculateAgeInMonths, validateDateComponents, validateFullDate, validateFutureDate, validateMinAgeMonths, validateMaxAge, internalErrorMessages, internalErrorMessage, externalErrorMessage, showError, sanitizeNumbers, addLeadingZero, onInput, isValid }
}

})